/** The ICE servers of every WebRTC surface: the STUN list, plus this member's TURN server. */

import { dequal } from 'dequal';
import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';

import { snapshotAtoms } from '../snapshotAtoms';
import { api } from '@/api';
import { ApiError } from '@/api/apiError';
import { HTTP_STATUS } from '@/api/httpStatus';
import { MINUTE_MS } from '@/lib/timeUnits';
import { getIceServers, kFallbackIceServers } from '@/lib/webrtc/iceServers';
import type { TurnCredentials } from '@/types/turn';

/** The member's TURN credentials as fetched. */
interface TurnSnapshot {
    /** Null until a load answers. */
    credentials: TurnCredentials | null;
    isLoading: boolean;
    error: Error | null;
}

/**
 * The waits before the retries of a failed load. They are short so that a load that raced the
 * login recovers fast. After the last one, the next load is the refresh tick.
 */
export const TURN_RETRY_DELAYS_MS = [3000, 10_000, 30_000];

const PENDING: TurnSnapshot = {
    credentials: null,
    isLoading: true,
    error: null,
};

/** A network failure or a server fault can pass; a 501 means the TURN server is not set up. */
function isRetryable(error: unknown): boolean {
    if (error instanceof ApiError) {
        return error.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR &&
            error.status !== HTTP_STATUS.NOT_IMPLEMENTED;
    }
    return error instanceof TypeError;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject): void => {
        if (signal.aborted) {
            reject(signal.reason);
            return;
        }
        const onAbort = (): void => {
            clearTimeout(timer);
            reject(signal.reason);
        };
        const timer = setTimeout((): void => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal.addEventListener('abort', onAbort, { once: true });
    });
}

async function loadTurnCredentials(signal: AbortSignal): Promise<TurnSnapshot> {
    for (const delayMs of TURN_RETRY_DELAYS_MS) {
        try {
            // eslint-disable-next-line no-await-in-loop -- each retry waits for the failure
            return await fetchSnapshot();
        } catch (error) {
            if (!isRetryable(error)) {
                throw error;
            }
            // eslint-disable-next-line no-await-in-loop -- the waits run one after another
            await sleep(delayMs, signal);
        }
    }
    return fetchSnapshot();
}

async function fetchSnapshot(): Promise<TurnSnapshot> {
    return {
        credentials: await api.getTurnCredentials(),
        isLoading: false,
        error: null,
    };
}

const REFRESH_MS = 5 * MINUTE_MS;

const turn = snapshotAtoms<TurnSnapshot>(PENDING, loadTurnCredentials, {
    refreshEveryMs: REFRESH_MS,
});

const rawIceServersAtom = atom((get): RTCIceServer[] => {
    const { credentials } = get(turn.value);
    return credentials === null ? kFallbackIceServers : getIceServers(credentials);
});

/** The same array until the credentials change; the STUN list before the first answer. */
export const iceServersAtom = selectAtom(
    rawIceServersAtom, (servers): RTCIceServer[] => servers, dequal);
